import {defineStore} from "pinia";
import {
    createTransportVehicle,
    deleteTransportVehicle,
    getTransportVehicleById,
    getTransportVehiclePreview,
    listTransportVehicles,
    updateTransportVehicle,
    getTransportVehicleSchedule
} from "@/services/transport-vehicle/transport-vehicle-service";
import type {TransportVehicle, TransportVehicleListRequest} from "@/models/transport-vehicle/transport-vehicle";
import {DefaultTransportVehicle} from "@/models/transport-vehicle/transport-vehicle";
import {DefaultPaginationItems, defaultTake, type PaginationItems} from "@/models/pagination";
import {DefaultDrawer, type Drawer} from "@/models/drawer";
import { getBrands, getByLicensePlate } from '@/services/vehicle/vehicle-service';
import type {Modal} from "@/models/modal";
import {DefaultModal} from "@/models/modal";
import type {TransportVehiclePreviewModel} from "@/models/transport-vehicle/transport-vehicle-preview";
import {DefaultTransportVehiclePreviewModel} from "@/models/transport-vehicle/transport-vehicle-preview";
import type { TransportVehicleSchedule, TransportVehicleScheduleRequest } from '@/models/transport-vehicle/transport-vehicle-schedule';
import { addCacheItem, type BrowserCache, clearExpiredItems, getCacheItem } from '@/helpers/cache/cache';
import { type RemovableRef, useStorage } from '@vueuse/core';
import type { IOptionModel } from '@/models/option';
import { DefaultFilterRequest } from "@/models/filter";
import { getRelation, listRelationAssignOptions } from '@/services/relation/relation-service';
import type { RelationAssignListRequest } from '@/models/relation/relation-assign-drawer';
import type { RelationListModel } from '@/models/relation/relation';
import { RelationType } from '@/constants/relation-type';
import { assignScheduleLayers } from '@/helpers/day-planner-helper';
import { TransportStatus } from '@/constants/transport-status';

interface TransportVehicleState {
    loading: boolean;
    transportVehicles: PaginationItems<TransportVehicle>;
    transportVehicleListRequest: TransportVehicleListRequest;
    transportVehicleDetailDrawer: Drawer;
    transportVehicle: TransportVehicle;
    transportVehicleDetail: TransportVehicle;
    transportVehicleSchedule: TransportVehicleSchedule[];
    transportVehicleScheduleRequest: TransportVehicleScheduleRequest;
    brandOptions: IOptionModel<string>[];
    brandCache: RemovableRef<BrowserCache<IOptionModel<string>[]>[]>;
    loadingBrands: boolean;
    transportVehicleStationOptions: IOptionModel<number>[];
    loadingTransportVehicleStations: boolean;
    transportVehicleStationListRequest: RelationAssignListRequest;
    transportVehicleStationPageCount: number;
    selectedCapacities: number[];
    /**
     * De concept-transporten die de planner in de dagplanner heeft aangevinkt, als input voor de
     * bulk-acties. Wordt nooit zelf opgeschoond: alles wat naar buiten gaat loopt via
     * effectiveConceptSelection, die snijdt op wat op dit moment ook echt in beeld staat.
     */
    selectedConceptTransportIds: number[];
    transportVehiclePreviewModal: Modal;
    transportVehiclePreviewModel: TransportVehiclePreviewModel;
}

// Copied from the getBrands logic in the vehicleStore.
const dayLifeTime = 24 * 60 * 60 * 1000;
const vehicleTypePassengerCar = 4;

export const useTransportVehicleStore = defineStore({
    id: 'transport-vehicle',
    state: () => ({
        loading: false,
        brandOptions: [],
        brandCache: useStorage("brand-options", []),
        loadingBrands: false,
        transportVehicleStationOptions: [],
        loadingTransportVehicleStations: false,
        transportVehicleStationListRequest: {
            take: defaultTake,
            page: 1,
            singleUseLocation: false,
            relationType: RelationType.TransportVehicleStation,
        },
        transportVehicleStationPageCount: 0,
        transportVehicles: DefaultPaginationItems(),
        transportVehicleListRequest: {
            filterOptions: DefaultFilterRequest(),
            page: 1,
            take: defaultTake,
            isActive: true
        },
        transportVehicleSchedule: [],
        transportVehicleScheduleRequest: {
            transportStartDate: new Date().setHours(0, 0, 0, 0),
            planned: true,
            notPlanned: true
        },
        selectedCapacities: [] as number[],
        selectedConceptTransportIds: [] as number[],
        transportVehicleDetailDrawer: DefaultDrawer(),
        transportVehicle: DefaultTransportVehicle(),
        transportVehicleDetail: DefaultTransportVehicle(),
        transportVehiclePreviewModal: DefaultModal(),
        transportVehiclePreviewModel: DefaultTransportVehiclePreviewModel()
    } as TransportVehicleState),
    getters: {
        availableCapacities(state): number[] {
            const capacities = state.transportVehicleSchedule
                .map((s) => s.transportVehicleCapacity ?? 0)
                .filter((c) => c > 0);
            return [...new Set(capacities)].sort((a, b) => a - b);
        },
        filteredTransportVehicleSchedule(state): TransportVehicleSchedule[] {
            if (state.selectedCapacities.length === 0) return state.transportVehicleSchedule;
            return state.transportVehicleSchedule.filter((schedule) => {
                const capacity = schedule.transportVehicleCapacity ?? 0;
                return state.selectedCapacities.includes(capacity);
            });
        },
        /** Alle concept-transporten die op dit moment in de dagplanner staan, in rijvolgorde. */
        selectableConceptTransportIds(): number[] {
            return this.filteredTransportVehicleSchedule
                .flatMap((schedule) => schedule.transports ?? [])
                .filter((transport) => transport.status === TransportStatus.ToPlan)
                .map((transport) => transport.id)
                .filter((id): id is number => id !== undefined);
        },
        /**
         * De selectie zoals die geldt: gesneden op wat nu in beeld staat. Een dagwissel of het
         * capaciteitsfilter kan een aangevinkt transport van het scherm halen, en dan mag de bottom-bar
         * het niet meetellen en de bulk-actie het niet meesturen — de planner zou anders ritten plannen
         * of verwijderen die hij niet heeft aangevinkt. Snijden gebeurt hier in plaats van in een watch
         * per oorzaak, zodat het één plek blijft.
         */
        effectiveConceptSelection(state): number[] {
            const selectable = new Set(this.selectableConceptTransportIds);
            return state.selectedConceptTransportIds.filter((id) => selectable.has(id));
        },
        hasConceptSelection(): boolean {
            return this.effectiveConceptSelection.length > 0;
        },
        allConceptsSelected(): boolean {
            const selectable = this.selectableConceptTransportIds;
            return selectable.length > 0 && this.effectiveConceptSelection.length === selectable.length;
        },
        /**
         * De concepten in één voertuigrij. Een rij kan meerdere gestapelde ritten hebben; de checkbox
         * staat per rij (zoals het ontwerp) en dekt ze dus allemaal.
         */
        conceptTransportIdsForVehicle(state) {
            return (transportVehicleId: number): number[] =>
                (state.transportVehicleSchedule.find((schedule) => schedule.id === transportVehicleId)?.transports ?? [])
                    .filter((transport) => transport.status === TransportStatus.ToPlan)
                    .map((transport) => transport.id)
                    .filter((id): id is number => id !== undefined);
        },
        isVehicleConceptSelected() {
            return (transportVehicleId: number): boolean => {
                const conceptIds = this.conceptTransportIdsForVehicle(transportVehicleId);
                return conceptIds.length > 0 && conceptIds.every((id) => this.effectiveConceptSelection.includes(id));
            };
        }
    },
    actions: {
        async getBrandOptions() {
            const identifier = `brands-${vehicleTypePassengerCar}`;
            this.loadingBrands = true;
            this.brandCache = clearExpiredItems(this.brandCache, dayLifeTime);

            const cachedItem = getCacheItem(this.brandCache, identifier);
            if (cachedItem) {
                this.brandOptions = cachedItem;
                this.loadingBrands = false;
                return;
            }

            const response = await getBrands();
            if (response.status === 200) {
                this.brandOptions = response.data;
                this.brandCache = addCacheItem(this.brandCache, identifier, this.brandOptions!);
            }
            this.loadingBrands = false;
        },
        async fetchTransportVehicles() {
            this.loading = true;
            this.transportVehicles = DefaultPaginationItems();

            const response = await listTransportVehicles(this.transportVehicleListRequest);
            if (response.status === 200) {
                this.transportVehicles = response.data;
            }

            this.loading = false;
        },
        async fetchTransportVehicleStationOptions() {
            this.loadingTransportVehicleStations = true;
            const request: RelationAssignListRequest = {
                ...this.transportVehicleStationListRequest,
                singleUseLocation: false,
                relationType: RelationType.TransportVehicleStation,
            };

            const response = await listRelationAssignOptions(request);
            if (response.status === 200) {
                const retrievedStationOptions = response.data.items.map((item: RelationListModel) => ({
                    value: item.id,
                    label: item.name,
                }));

                this.transportVehicleStationOptions = await this.ensureSelectedTransportVehicleStationOptionLoaded(retrievedStationOptions) ?? retrievedStationOptions;
                this.transportVehicleStationPageCount = response.data.pageCount;
            }

            this.loadingTransportVehicleStations = false;
        },
        async fetchAndAppendTransportVehicleStationOptions() {
            this.loadingTransportVehicleStations = true;

            const request: RelationAssignListRequest = {
                ...this.transportVehicleStationListRequest,
                singleUseLocation: false,
                relationType: RelationType.TransportVehicleStation,
            };

            const response = await listRelationAssignOptions(request);
            if (response.status === 200) {
                const retrievedStationOptions = response.data.items.map((item: RelationListModel) => ({
                    value: item.id,
                    label: item.name,
                }));

                const allStationOptions = [...this.transportVehicleStationOptions, ...retrievedStationOptions];
                this.transportVehicleStationOptions = await this.ensureSelectedTransportVehicleStationOptionLoaded(allStationOptions) ?? allStationOptions;
                this.transportVehicleStationPageCount = response.data.pageCount;
            }

            this.loadingTransportVehicleStations = false;
        },
        async ensureSelectedTransportVehicleStationOptionLoaded(retrievedOptions?: IOptionModel<number>[]): Promise<IOptionModel<number>[]> {
            const selectedStationId = this.transportVehicle.stationedAtId;
            const options = retrievedOptions ?? this.transportVehicleStationOptions ?? [];

            if (selectedStationId == null) {
                return options;
            }

            let selected = options.find(o => String(o.value) === String(selectedStationId));

            if (!selected) {
                const response = await getRelation(selectedStationId);
                if (response.status === 200) {
                    selected = {
                        value: response.data.id,
                        label: response.data.name,
                    };
                }
            }

            const merged = selected
                ? Array.from(new Map([selected, ...options].map(option => [String(option.value), option])).values())
                : options;

            if (!retrievedOptions) {
                this.transportVehicleStationOptions = merged;
            }
            return merged;
        },
        async fetchAndAppendTransportVehicles() {
            this.loading = true;

            const response = await listTransportVehicles(this.transportVehicleListRequest);
            if (response.status === 200) {
                const retrievedTransportVehicles = response.data.items;
                const allTransportVehicles = [...this.transportVehicles.items, ...retrievedTransportVehicles];

                this.transportVehicles.items = allTransportVehicles.filter((transportVehicle, index, self) =>
                    index === self.findIndex((o) => o.id === transportVehicle.id)
                );

                this.transportVehicles.totalItems = response.data.totalItems;
                this.transportVehicles.pageCount = response.data.pageCount;
            }

            this.loading = false;
        },
        openCreateDrawer() {
            this.transportVehicle = DefaultTransportVehicle();
            this.transportVehicleDetailDrawer.visible = true;
            this.transportVehicleDetailDrawer.loading = false;
        },
        async openUpdateDrawer(id: number) {
            this.transportVehicleDetailDrawer.loading = true;
            this.transportVehicleDetailDrawer.visible = true;

            const response = await getTransportVehicleById(id);
            if (response.status === 200) {
                this.transportVehicle = {
                    ...DefaultTransportVehicle(),
                    ...response.data
                };

                await this.ensureSelectedTransportVehicleStationOptionLoaded();
            } else {
                this.transportVehicleDetailDrawer.visible = false;
            }

            this.transportVehicleDetailDrawer.loading = false;
        },
        async createOrUpdateTransportVehicle() {
            this.transportVehicleDetailDrawer.loading = true;

            const id = Number(this.transportVehicle.stationedAtId);
            const payload: TransportVehicle = {
            ...this.transportVehicle,
            stationedAtId: id > 0 ? id : null,
            };

            let response = undefined;
            if (this.transportVehicle?.id != 0) {
                response = await updateTransportVehicle(payload);
            } else {
                response = await createTransportVehicle(payload);
            }

            if (response.status == 200) {
                this.transportVehicle = {
                    ...DefaultTransportVehicle(),
                    ...response.data
                };
                this.transportVehicleDetailDrawer.visible = false;
            }

            this.transportVehicleDetailDrawer.loading = false;

            return response;
        },
        async getTransportVehicleDetail(id: number) {
            this.transportVehicleDetail = DefaultTransportVehicle();
            this.loading = true;
            const response = await getTransportVehicleById(id);
            if (response.status === 200) {
                this.transportVehicleDetail = {
                    ...DefaultTransportVehicle(),
                    ...response.data
                };
            }
            this.loading = false;
            return response.status === 200;
        },
        async getByLicensePlate() {
            this.transportVehicleDetailDrawer.loading = true;
            const response = await getByLicensePlate(this.transportVehicle.country, this.transportVehicle.licensePlate)
            this.transportVehicleDetailDrawer.loading = false;
            if (response.status === 200) {
                return response.data;
            }
            return null;
        },
        async deleteTransportVehicle() {
            if (!this.transportVehicle.id) return;

            this.transportVehicleDetailDrawer.loading = true;
            const response = await deleteTransportVehicle(this.transportVehicle.id);
            this.transportVehicleDetailDrawer.loading = false;

            return response;
        },
        async openTransportVehiclePreview(id: number) {
            this.transportVehiclePreviewModal.loading = true;
            this.transportVehiclePreviewModal.visible = true;
            this.transportVehiclePreviewModel = DefaultTransportVehiclePreviewModel();

            const response = await getTransportVehiclePreview(id);

            if (response.status == 200) {
                this.transportVehiclePreviewModel = response.data;
            } else {
                this.transportVehiclePreviewModal.visible = false;
            }

            this.transportVehiclePreviewModal.loading = false;
        },
        async getTransportVehicleSchedule() {
            this.loading = true;
            const response = await getTransportVehicleSchedule(this.transportVehicleScheduleRequest);
            if (response.status === 200) {
                // De laagverdeling hoort bij het ophalen: elke plek die het schema herlaadt (datumwissel,
                // een afgeronde planrun) heeft de lagen nodig, anders stapelen overlappende transporten
                // allemaal op laag 0.
                this.transportVehicleSchedule = assignScheduleLayers(response.data as TransportVehicleSchedule[]);
            }
            this.loading = false;
        },
        /**
         * Vinkt een voertuigrij aan of uit. Half-aangevinkt bestaat niet: alle concepten in de rij gaan
         * mee, en zodra er één ontbreekt geldt de rij als niet-geselecteerd en zet aanvinken ze allemaal.
         */
        toggleVehicleConceptSelection(transportVehicleId: number) {
            const conceptIds = this.conceptTransportIdsForVehicle(transportVehicleId);
            if (conceptIds.length === 0) return;

            if (this.isVehicleConceptSelected(transportVehicleId)) {
                this.selectedConceptTransportIds = this.selectedConceptTransportIds
                    .filter((id) => !conceptIds.includes(id));
                return;
            }

            this.selectedConceptTransportIds = [...new Set([...this.selectedConceptTransportIds, ...conceptIds])];
        },
        selectAllConcepts() {
            this.selectedConceptTransportIds = [...this.selectableConceptTransportIds];
        },
        clearConceptSelection() {
            this.selectedConceptTransportIds = [];
        }
    }
})

